// src/components/auth/CustomLoginPage.tsx
import React, { useState } from 'react';
import { useLogin, useNotify, useTranslate, useRedirect } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {PersonRounded, LockRounded} from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import logo from '@/assets/images/roommatik_logo.webp';

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.custom.login.containerGradient(theme.palette),
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const LoginBox = styled(Paper)(({ theme }) => ({
  width: '90%',
  maxWidth: 440,
  borderRadius: theme.custom.radius.large,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.custom.shadows.login,
  position: 'relative',
  borderTop: `8px solid ${theme.palette.primary.light}`,
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.custom.radius.large,
    maxWidth: '90%',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(8, 4, 4),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3, 3),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 6, 6),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 3, 4),
  },
}));

const CustomLoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();
  const redirect = useRedirect();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleInputChange = (field: 'username' | 'password') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    
    try {
      await login({ 
        username: formData.username, 
        password: formData.password 
      });
      
      // Obtener la URL de redirecciÃ³n desde el estado de la ubicaciÃ³n
      // o usar el dashboard por defecto
      const redirectTo = location.state?.from?.pathname || '/';
      
      // Limpiar la URL para evitar que quede /login en la ruta
      if (window.location.pathname.includes('/login')) {
        navigate(redirectTo, { replace: true });
      } else {
        redirect(redirectTo);
      }
      
    } catch (error: any) {
      const errorMessage = error?.message || translate('ra.auth.sign_in_error');
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePassword = (): void => {
    setShowPassword(prev => !prev);
  };

  const isFormValid = formData.username.length > 0 && formData.password.length > 0;

  return (
    <LoginContainer>
      <LoginBox>
        <LogoContainer>
          <Box
            component="img"
            src={logo}
            alt="Logo de Roommatik"
            sx={{
              width: isMobile ? 120 : 380,
              height: 'auto',
              maxHeight: 90,
              mb: 4,
              filter: theme.custom.login.logoFilter,
              objectFit: 'contain',
            }}
          />
           <Typography variant={isMobile ? "h5" : "h3"}>Locker Management</Typography>
          <Typography variant="body1">{translate(`pages.login.subtitle`)}</Typography>
        </LogoContainer>
        
        <FormContainer>
        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <TextField
            label={translate('ra.auth.username')}
            variant="outlined"
            value={formData.username}
            onChange={handleInputChange('username')}
            autoComplete="username"
            autoFocus
            disabled={isLoading}
            size='medium'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonRounded />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? '100%' : '90%',
              mb: isMobile ? 2 : 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.custom.login.fieldBackground(theme.palette),
              },
            }}
          />

          <TextField
            label={translate('ra.auth.password')}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            value={formData.password}
            onChange={handleInputChange('password')}
            autoComplete="current-password"
            disabled={isLoading}
            size='medium'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockRounded />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePassword}
                    edge="end"
                    disabled={isLoading}
                    sx={{border:'none', background:'none', color: theme.palette.text.secondary}}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? '100%' : '90%',
              mb: isMobile ? 2 : 3,
            
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.custom.login.fieldBackground(theme.palette),
              },
            }}
          />

          <Button
            variant="contained"
            type="submit"
            disabled={isLoading || !isFormValid}
            sx={{ 
              width: isMobile ? '100%' : '100%',
              mt: isMobile ? 1 : 2,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {isLoading 
              ? translate('ra.auth.logging_in') 
              : translate('pages.login.login_button', 'Iniciar Sesión')
            }
          </Button>
        </Box>
      </FormContainer>

      </LoginBox>
    </LoginContainer>
  );
};

export default CustomLoginPage;